/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, { useCallback, useMemo } from 'react';
import { View, useWindowDimensions } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useLogger } from '@airasia-rn/core-logger';
import { useDeviceInfo } from '@airasia-rn/ui';
import SkeletonLoaderView from '@airasia-phoenix/react-native/components/SkeletonLoaderView';
import { HotelBanner } from '@airasia-rn/hotels/components';
import { useSnapNavigation } from '../../context/SnapNavigationContext';

import { useSnapBanners } from '../../hooks/snap/search';
import {
  SNAP_BANNER_AUTO_SCROLL_INTERVAL,
  SNAP_BANNER_HEIGHT,
} from '../../configs/constants';
import { parseBannerData, getDefaultBanner } from '../../utils/bannerUtils';
import type { SnapBannerItem } from '../../types/banner';
import { useSnapConfig } from '../../configs/SnapConfigProvider';
import { SnapSearchWidgetEvent } from '../../analytics';
import { EVENT_NAMES } from '../../analytics/snapSearchWidget/snapSearchWidget.meta';

type SnapBannerViewProps = {
  testID?: string;
};

const SnapBannerView = ({ testID = 'snap-banner' }: SnapBannerViewProps) => {
  const { width: screenWidth } = useWindowDimensions();
  const { logEvent } = useLogger();
  const { handleRedirection } = useSnapNavigation();
  const { config: snapConfig } = useSnapConfig();
  const { geoId } = snapConfig;
  const { isWeb, isMobileWidth } = useDeviceInfo();

  const isCountryBannerEnabled = true; // temporarily enabled
  const { data: bannerData, isLoading, isError } = useSnapBanners();

  const banners = useMemo(() => {
    const fallbackBanner = getDefaultBanner();
    if (isError && !bannerData) {
      return [fallbackBanner];
    }

    const parsedBanners = parseBannerData(
      bannerData,
      geoId,
      isCountryBannerEnabled,
    );
    if (parsedBanners.length === 0) {
      return [fallbackBanner];
    }

    return parsedBanners;
  }, [bannerData, isCountryBannerEnabled, isError, geoId]);

  const handleBannerView = useCallback(
    (item: SnapBannerItem, index: number) => {
      const params = SnapSearchWidgetEvent.snapBannerViewEvent({
        bannerName: item.name || item.id,
        position: index + 1,
      });
      logEvent(EVENT_NAMES.VIEW, params);
    },
    [logEvent],
  );

  const handleBannerPress = useCallback(
    (item: SnapBannerItem, index: number) => {
      const params = SnapSearchWidgetEvent.snapBannerTapEvent({
        bannerName: item.name || item.id,
        position: index + 1,
      });
      logEvent(EVENT_NAMES.TAP, params);

      if (!item.redirectionUrl) return;

      handleRedirection(item.redirectionUrl);
    },
    [handleRedirection, logEvent],
  );

  const isDesktopWeb = isWeb && !isMobileWidth;
  if (isDesktopWeb) {
    return null;
  }

  if (isLoading) {
    return (
      <View testID={`${testID}-skeleton`} style={styles.skeletonContainer}>
        <SkeletonLoaderView
          style={[
            styles.skeleton,
            { width: screenWidth, height: SNAP_BANNER_HEIGHT },
          ]}
        />
      </View>
    );
  }

  return (
    <View testID={testID} style={styles.container}>
      <HotelBanner
        banners={banners}
        testID={`${testID}-carousel`}
        autoScrollInterval={SNAP_BANNER_AUTO_SCROLL_INTERVAL}
        onBannerPress={handleBannerPress}
        onBannerView={handleBannerView}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
    display: { xs: 'flex', md: 'none' },
  },
  skeletonContainer: {
    width: '100%',
    height: SNAP_BANNER_HEIGHT,
    display: { xs: 'flex', md: 'none' },
  },
  skeleton: {
    width: '100%',
    height: SNAP_BANNER_HEIGHT,
  },
});

export default SnapBannerView;
